import { useEffect, useMemo, useState } from "react";
import { useSeo } from "@/hooks/useSeo";
import PageHeader from "@/components/feature/PageHeader";
import WorkCard from "@/components/feature/WorkCard";
import { worksList, type WorkCategory, type RoleType } from "@/mocks/works";

const PAGE_STEP = 6;
const CATEGORY_OPTIONS: Array<WorkCategory | "全部"> = ["全部", "电视剧", "电影"];
const ROLE_OPTIONS: Array<RoleType | "全部"> = ["全部", "主演", "配角"];

export default function WorksPage() {
  const [category, setCategory] = useState<WorkCategory | "全部">("全部");
  const [year, setYear] = useState<string>("全部");
  const [role, setRole] = useState<RoleType | "全部">("全部");
  const [sort, setSort] = useState<"time" | "popularity">("time");
  const [visible, setVisible] = useState(PAGE_STEP);
  const [showFilters, setShowFilters] = useState(false);

  useSeo({
    title: "影视作品大全 - 佐佐木明希参演汇总",
    description:
      "佐佐木明希参演的电视剧与电影大全，支持按作品类型、上映年份与角色类型筛选，并提供按时间与热度的排序，快速定位你想看的作品。",
    keywords: "佐佐木明希作品,电视剧,电影,参演作品,作品列表",
  });

  const years = useMemo(
    () => Array.from(new Set(worksList.map((w) => String(w.year)))).sort((a, b) => Number(b) - Number(a)),
    []
  );

  const filtered = useMemo(() => {
    const list = worksList.filter((w) => {
      if (category !== "全部" && w.category !== category) return false;
      if (year !== "全部" && String(w.year) !== year) return false;
      if (role !== "全部" && w.roleType !== role) return false;
      return true;
    });
    return list.sort((a, b) =>
      sort === "time" ? b.year - a.year : b.popularity - a.popularity
    );
  }, [category, year, role, sort]);

  useEffect(() => {
    setVisible(PAGE_STEP);
  }, [category, year, role, sort]);

  const visibleItems = filtered.slice(0, visible);
  const hasMore = visible < filtered.length;

  const resetFilters = () => {
    setCategory("全部");
    setYear("全部");
    setRole("全部");
    setSort("time");
  };

  return (
    <>
      <PageHeader
        crumbs={[{ label: "首页", path: "/" }, { label: "影视作品" }]}
        title="影视作品"
        lead="浏览佐佐木明希参演的电视剧与电影。你可以按作品类型、上映年份、角色类型进行筛选，也可以按时间或热度排序。"
      />

      <section className="bg-background-50 py-10 md:py-14">
        <div className="mx-auto w-full max-w-[1200px] px-4 md:px-6">
          <div className="rounded-lg border border-background-200 bg-background-100 p-4 md:p-5">
            <div className="flex items-center justify-between gap-4">
              <span className="inline-flex items-center gap-2 text-sm font-label font-medium text-foreground-700">
                <i className="ri-filter-3-line text-primary-600" aria-hidden="true" />
                筛选与排序
                <span className="text-foreground-400">共 {filtered.length} 部</span>
              </span>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setShowFilters((v) => !v)}
                  className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-sm text-foreground-700 transition-colors hover:border-primary-400 md:hidden"
                >
                  {showFilters ? "收起" : "展开"}
                  <i className={showFilters ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="inline-flex items-center gap-1 whitespace-nowrap text-sm text-foreground-500 transition-colors hover:text-primary-600"
                >
                  <i className="ri-refresh-line" aria-hidden="true" />
                  清空筛选
                </button>
              </div>
            </div>

            <div className={`${showFilters ? "block" : "hidden"} mt-4 space-y-4 md:mt-5 md:block`}>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                <span className="w-16 shrink-0 pt-1 text-sm text-foreground-500">类型</span>
                <div className="flex flex-wrap gap-2">
                  {CATEGORY_OPTIONS.map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => setCategory(option)}
                      className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-label transition-colors ${
                        category === option
                          ? "bg-primary-500 text-background-50"
                          : "bg-background-50 text-foreground-700 hover:text-primary-600"
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                <span className="w-16 shrink-0 pt-1 text-sm text-foreground-500">年份</span>
                <div className="flex flex-wrap gap-2">
                  {["全部", ...years].map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => setYear(option)}
                      className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-label transition-colors ${
                        year === option
                          ? "bg-secondary-500 text-background-50"
                          : "bg-background-50 text-foreground-700 hover:text-secondary-700"
                      }`}
                    >
                      {option === "全部" ? "全部年份" : option}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                <span className="w-16 shrink-0 pt-1 text-sm text-foreground-500">角色</span>
                <div className="flex flex-wrap gap-2">
                  {ROLE_OPTIONS.map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => setRole(option)}
                      className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-label transition-colors ${
                        role === option
                          ? "bg-secondary-500 text-background-50"
                          : "bg-background-50 text-foreground-700 hover:text-secondary-700"
                      }`}
                    >
                      {option === "全部" ? "全部角色" : option}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-3 border-t border-background-200 pt-4 sm:flex-row sm:items-center">
                <span className="w-16 shrink-0 text-sm text-foreground-500">排序</span>
                <div className="inline-flex rounded-full bg-background-50 p-1">
                  <button
                    type="button"
                    onClick={() => setSort("time")}
                    className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-label transition-colors ${
                      sort === "time" ? "bg-accent-500 text-foreground-950" : "text-foreground-700"
                    }`}
                  >
                    按上映时间
                  </button>
                  <button
                    type="button"
                    onClick={() => setSort("popularity")}
                    className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-label transition-colors ${
                      sort === "popularity" ? "bg-accent-500 text-foreground-950" : "text-foreground-700"
                    }`}
                  >
                    按作品热度
                  </button>
                </div>
              </div>
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="mt-10 rounded-lg border border-dashed border-background-300 bg-background-100 py-20 text-center">
              <i className="ri-film-line text-4xl text-foreground-400" aria-hidden="true" />
              <p className="mt-4 font-heading text-lg text-foreground-800">暂无影视作品</p>
              <p className="mt-2 text-sm text-foreground-500">当前筛选条件下没有匹配的作品</p>
              <button
                type="button"
                onClick={resetFilters}
                className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-refresh-line" aria-hidden="true" />
                清空筛选
              </button>
            </div>
          ) : (
            <>
              <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {visibleItems.map((item) => (
                  <WorkCard key={item.id} item={item} />
                ))}
              </div>
              <div className="mt-10 flex justify-center">
                {hasMore ? (
                  <button
                    type="button"
                    onClick={() => setVisible((v) => v + PAGE_STEP)}
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-primary-300 px-6 py-3 text-sm font-label font-medium text-primary-700 transition-colors hover:bg-primary-50"
                  >
                    加载更多作品
                    <i className="ri-add-line" aria-hidden="true" />
                  </button>
                ) : (
                  <p className="text-sm text-foreground-500">
                    已显示全部 {filtered.length} 部作品
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </section>
    </>
  );
}